import { useEffect, useReducer } from "react";
import initialState, { AccountStateType } from "../state/InitialState";
import { appInsights } from "../services/AppInsights";
import axios from "axios";
import { ASLoginUrl } from "../helpers/constants";
import fetchAccountToken from "../services/AccountTokenService";

let MPChoiceServiceURL = process.env.REACT_APP_MPCHOICESERVICE_ENDPOINT;


type ActionType =
  | { type: "setTokenValue"; value: (typeof initialState)["Token"] }
  | {
      type: "setMileagePlanDetails";
      value: (typeof initialState)["MileagePlanDetails"];
    }
  | { type: "setError"; value: (typeof initialState)["Error"] }
  | { type: "setLoading"; value: (typeof initialState)["isLoadingToken"] };

export const reducer = (state: AccountStateType, action: ActionType): AccountStateType => {
  switch (action.type) {
    case "setTokenValue":
      return {
        ...state,
        Token: {
          ...action.value,
        },
      };
    case "setMileagePlanDetails":
      return {
        ...state,
        MileagePlanDetails: {
          ...action.value,
        },
      };
    case "setError":
      return {
        ...state,
        Error: action.value,
      };
    case "setLoading":
      return {
        ...state,
        isLoadingToken: action.value,
      };
    default:
      return state;
  }
};

const MPChoiceService = () => {
  let [accountState, accountDataDispatch] = useReducer(reducer, initialState);

  // Get account guid data from token
  let fetchDataFromToken = async () => {
    await fetchAccountToken({
      setLoading: (isLoading: boolean) => {
        accountDataDispatch({
          type: "setLoading",
          value: isLoading,
        });
      },
      handleNotLoggedIn: () => {
        redirect(ASLoginUrl);
      },
      handleSessionExpired: () => {
        redirect(ASLoginUrl);
      },
      handleSuccess: (data: any) => {
        const { token, accountGuid, alaskaLoyaltyNumber, isSuperUser } = data;
        accountDataDispatch({
          type: "setTokenValue",
          value: {
            Value: token,
            Guid: accountGuid,
            MileagePlanNumber: alaskaLoyaltyNumber,
            IsSuperUser: isSuperUser,
          },
        });
      },
      handleError: (error: any) => {
        accountDataDispatch({
          type: "setError",
          value: error,
        });
      },
    });
  };

  const redirect = (path: string) => {
    window.location.href = path;
  };

  const getMPChoiceService = async () => {
    accountDataDispatch({
      type: "setLoading",
      value: true,
    });
    if (accountState?.Token?.Guid && accountState?.Token?.Value) {
      try {
        const MPChoiceServiceURLEndpoint = `${MPChoiceServiceURL}?accountGuid=${accountState.Token.Guid}`;
        const { data } = await axios.get(MPChoiceServiceURLEndpoint, {
          headers: {
            Authorization: `Bearer ${accountState.Token.Value}`,
            "Ocp-Apim-Subscription-Key": process.env.REACT_APP_MPCHOICE_BFF_KEY as string,
          },
        });

        const MileagePlanData = data;
        appInsights?.trackEvent({
          name: "getMPChoiceService service called for MP# " + MileagePlanData.MileagePlanNumber,
        });
        const accountDataDetails: AccountStateType["MileagePlanDetails"] = {
          MileagePlanNumber: MileagePlanData.mileagePlanNumber,
          FirstName: MileagePlanData.firstName,
          LastName: MileagePlanData.lastName,
          TierName: MileagePlanData.tierName,
          TierCode: MileagePlanData.tierCode,
          MemberBalance: +MileagePlanData.miles,
          ChoiceSelectedStatus: MileagePlanData.mP100KChoice,
          TierEndDate: MileagePlanData.tierEndDate,
          Email: MileagePlanData.email,
          HowTier: MileagePlanData.howTier,
          miles: MileagePlanData.organicEQMs,
          MemberSelectionId: MileagePlanData.memberSelectionId,
          MilestoneId: MileagePlanData.milestoneId,
          MilestoneName: MileagePlanData.milestoneName,
          MP100KChoice: MileagePlanData.mP100KChoice,
          MilestoneAchievementDate: MileagePlanData.milestoneAchievementDate,
          SelectionExpirationDate: MileagePlanData.selectionExpirationDate,
          IsEligibleFor100KChoiceSelection: MileagePlanData.isEligibleFor100KChoiceSelection,
          Perks: MileagePlanData.Perks,
          asoaMiles: +MileagePlanData.asoaMiles,
          oneWorldTier: MileagePlanData.oneWorldTier,
        };
        accountDataDispatch({
          type: "setMileagePlanDetails",
          value: accountDataDetails,
        });
      } catch (ex: any) {
        appInsights?.trackException({ exception: ex });
        accountDataDispatch({
          type: "setError",
          value: new Error("Unable to retrieve your Mileage plan Information.") as any,
        });
      } finally {
        accountDataDispatch({
          type: "setLoading",
          value: false,
        });
      }

      // Useful to know if logged in during development
      if (
        process.env.REACT_APP_RELEASE_ENV === "dev" ||
        process.env.REACT_APP_RELEASE_ENV === "test" ||
        process.env.REACT_APP_RELEASE_ENV === "qa"
      ) {
        console.log("Logged in to as.com :-)");
      }

      // Useful to get login prompt during development
    } else if (
      process.env.REACT_APP_RELEASE_ENV === "dev" ||
      process.env.REACT_APP_RELEASE_ENV === "test" ||
      process.env.REACT_APP_RELEASE_ENV === "qa"
    ) {
      console.log("Did you sign in to as.com?");
    }
  };

  useEffect(() => {
    fetchDataFromToken();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getMPChoiceService();
    // eslint-disable-next-line
  }, [accountState.Token]);

  return {
    accountState,
    accountDataDispatch,
  };
};

export default MPChoiceService;
